/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { IOrderSummaryLines } from '@msdyn365-commerce-modules/order-summary-utilities';
import { Alert, INodeProps, Node } from '@msdyn365-commerce-modules/utilities';
import { IDictionary } from '@msdyn365-commerce/core';
import * as React from 'react';
import { ICartlinesViewProps } from './components/smwe-cart-line-items';
import { ICartViewProps } from './smwe-cart';

const _renderCartlines = (
  cartLines: ICartlinesViewProps[] | undefined,
  cartEmptyText: string,
  CartlinesWrapper: INodeProps,
  storeSelector: React.ReactNode | undefined,
  backToShoppingButton: React.ReactNode,
  waitingComponent: React.ReactNode,
  cartLoadingStatus: string,
  cartDataResult: boolean,
  itemsTitle: string,
  qtyTitle: string,
  unitPriceTitle: string,
  totalTitle: string
): JSX.Element[] | JSX.Element => {
  if (cartLoadingStatus) {
    return <>{cartLoadingStatus}</>;
  }
  if (cartLines) {
    return (
      <table>
        <thead className='cart-lineitems-header'>
          <th scope='col' className='header-items'>{itemsTitle}</th>
          <th scope='col' className='header-qty'>{qtyTitle}</th>
          <th scope='col' className='header-unitprice'>{unitPriceTitle}</th>
          <th scope='col' className='header-total'>{totalTitle}</th>
        </thead>
        <tbody>
          <div className='cart-lineitems-container'>
            {cartLines.map((cartLine, index) => {
              return (
                <div className='msc-cart-lines-item' key={index}>
                  {cartLine.cartline}
                  {storeSelector && cartLine.pickUpInStore ? (
                    <Node {...cartLine.pickUpInStore.ContainerProps}>
                      {cartLine.pickUpInStore.defaultComponent}
                    </Node>
                  ) : null}
                  {cartLine.addToWishlist}
                </div>
              );
            })}
          </div>
        </tbody>
      </table>
    );
  } else {
    return cartDataResult ? (
      <div className='msc-cart__empty-cart'>
        <p className='msc-cart-line'>{cartEmptyText}</p>
        {backToShoppingButton}
      </div>
    ) : (
      <>{waitingComponent}</>
    );
  }
};

const _renderOrderSummarylines = (
  orderSummaryLines: IOrderSummaryLines | undefined,
  OrderSummaryItems: INodeProps,
  props: ICartViewProps
): JSX.Element | null => {
  if (!orderSummaryLines) {
    return null;
  }
  return (
    <Node {...OrderSummaryItems}>
      {orderSummaryLines.subtotal}
      <div className='sr-only'></div>
      {orderSummaryLines.shipping}
      <div className='sr-only'></div>
      {orderSummaryLines.tax}
      <div className='sr-only'></div>
      {orderSummaryLines.totalDiscounts
        ? orderSummaryLines.totalDiscounts
        : null}
        <div className='sr-only'></div>
      {orderSummaryLines.orderTotal}
      <div className='sr-only'></div>
      <p className='cart-order-summary-asterix'>{props.resources.conditionsText}</p>
    </Node>
  );
};

// Detect invalid items in the cart. Items are flagged externally via the Comment field
const _isInvalidCart = (cartLinesInvalid: IDictionary<boolean>): boolean => {

  for (const lineId of Object.keys(cartLinesInvalid)) {
    if (cartLinesInvalid[lineId]) {
      return true;
    }
  }

  return false;
};

//==================================================
//==================================================
const CartView: React.FC<ICartViewProps> = (props: ICartViewProps) => {
  const preventCheckout = _isInvalidCart(props.cartLineStatus) || props.mixedCart.isMixedCart;

  return (
    <div
      className={props.className}
      id={props.id}
      {...props.renderModuleAttributes(props)}
    >
      {props.title}
      <Alert isOpen={props.cartlines !== undefined && props.mixedCart && props.mixedCart.isMixedCart} className='cart-error' color='danger' assertive={true} >
        <div className='cart-error-wrapper'>
          <div className='msc-alert__header' aria-hidden='true'>
            <span className='msi-exclamation-triangle' />
            <span>{props.resources.mixModeCartError}</span>
          </div>
        </div>
      </Alert>
      <Node {...props.CartlinesWrapper}>
        {_renderCartlines(
          props.cartlines,
          props.resources.emptyCartText,
          props.CartlinesWrapper,
          props.storeSelector,
          props.backToShoppingButton,
          props.waitingComponent,
          props.cartLoadingStatus,
          props.cartDataResult,
          props.resources.itemsTitle,
          props.resources.qtyTitle,
          props.resources.unitPriceTitle,
          props.resources.totalTitle
        )}
      </Node>
      {props.content &&
        props.content.map(
          (contentSlotData: React.ReactNode, index: number) => {
            return (
              <div className='col-12' key={index}>
                {contentSlotData}
              </div>
            );
          }
        )}
      {props.orderSummaryHeading && (
        <Node {...props.OrderSummaryWrapper}>
          {_renderOrderSummarylines(
            props.orderSummaryLineitems,
            props.OrderSummaryItems,
            props
          )}
          <Node className='checkout-btn-wrap'>
            {props.backToShoppingButton}
            {preventCheckout ? props.checkoutButtonDisabled : props.checkoutAsSignInUserButton}
          </Node>
          <div className='promo-code-wrap'>
            {props.promoCode}
          </div>
        </Node>
      )}
      {props.storeSelector}
    </div>
  );
};

export default CartView;